import { CartFund, PRODUCTS, ProductType, KorpoData } from 'cart-model';
import { CurrentLimits, PensionProductCode } from 'funds-model';

export enum AmountValidationErrorType {
  NONE = 'none',
  MIN = 'min',
  MAX = 'max',
}

export interface AmountValidationError {
  type: AmountValidationErrorType;
  productType: ProductType;
  min?: number;
  max?: number;
}

export interface CartValidationResult {
  valid: boolean;
  productTypeToValidationError: Record<ProductType, AmountValidationError>;
  cartFundToValidationError: Map<CartFund, AmountValidationError>;
}

function validateAmount(amount: number, productType: ProductType, cartFundId: number, korpoData?: KorpoData): AmountValidationError {
  const product = PRODUCTS[productType];
  let minAmount = product.minimalAmount;
  if (korpoData != null && korpoData.allowedFunds != null) {
    const korpoFunds = korpoData.allowedFunds;
    const korpoFund = korpoFunds.find(kf => kf.fundId === cartFundId);
    if (korpoFund != null && korpoFund.minPurchaseOpening != null) {
      minAmount = korpoFund.minPurchaseOpening;
    }
  }

  const type = amount < minAmount ? AmountValidationErrorType.MIN : AmountValidationErrorType.NONE;
  return { type, productType, min: minAmount };
}

function getLimit(currentLimits: CurrentLimits, productCode: PensionProductCode | undefined, isEntrepreneur: boolean): number {
  if (!productCode) {
    return Infinity;
  } else {
    const limits = currentLimits.currentLimits[productCode];
    if (isEntrepreneur && productCode === PensionProductCode.ikze && limits.entrepreneurLimit) {
      return limits.entrepreneurLimit;
    } else {
      return limits.limit || Infinity;
    }
  }
}

function validateSumAmount(cartFunds: CartFund[], productType: ProductType, currentLimits: CurrentLimits,
                           isEntrepreneur: boolean): AmountValidationError {
  const filteredCartFunds = cartFunds.filter(cartFund => cartFund.productType === productType);
  const product = PRODUCTS[productType];
  const productCode = product.productCode;
  const max = getLimit(currentLimits, productCode, isEntrepreneur);
  const sum = filteredCartFunds.reduce((s, cartFund) => s + cartFund.amount, 0);

  const type = sum > max ? AmountValidationErrorType.MAX : AmountValidationErrorType.NONE;
  return { type, productType, max };
}

export function validateCart(cartFunds: CartFund[], currentLimits: CurrentLimits, isEntrepreneur = false, korpoData?: KorpoData): CartValidationResult {
  const productTypeToValidationError = {
    [ProductType.funds]: validateSumAmount(cartFunds, ProductType.funds, currentLimits, isEntrepreneur),
    [ProductType.ike]: validateSumAmount(cartFunds, ProductType.ike, currentLimits, isEntrepreneur),
    [ProductType.ikze]: validateSumAmount(cartFunds, ProductType.ikze, currentLimits, isEntrepreneur),
  };
  const cartFundToValidationError =
      cartFunds.reduce((cartFundsToValidationErrors, cartFund) =>
              cartFundsToValidationErrors.set(cartFund, validateAmount(cartFund.amount, cartFund.productType, cartFund.fund.id, korpoData)),
          new Map<CartFund, AmountValidationError>());
  const valid = cartFunds.every(cartFund => cartFundToValidationError.get(cartFund)!.type === AmountValidationErrorType.NONE)
      && productTypeToValidationError[ProductType.funds].type === AmountValidationErrorType.NONE
      && productTypeToValidationError[ProductType.ike].type === AmountValidationErrorType.NONE
      && productTypeToValidationError[ProductType.ikze].type === AmountValidationErrorType.NONE;
  return { valid, productTypeToValidationError, cartFundToValidationError };
}
